import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useId, useState } from 'react';

interface AccountMenuState {
  anchor: HTMLElement | null;
  id: string;
  open: (event: { currentTarget: HTMLElement }) => void;
  close: () => void;
  /** Spread onto the trigger so its ARIA state cannot drift from the menu. */
  triggerProps: {
    'aria-controls': string | undefined;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
  };
}

/** The account control lives in the sidebar from `md` up and in the header
 *  below it. Exactly one may be mounted: a `display:none` copy is invisible but
 *  still answers to screen readers and to `getByRole`. */
export const useAccountSurface = (): 'header' | 'sidebar' => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('md')) ? 'sidebar' : 'header';
};

export const useAccountMenu = (): AccountMenuState => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();

  const open = useCallback((event: { currentTarget: HTMLElement }): void => {
    setAnchor(event.currentTarget);
  }, []);

  const close = useCallback((): void => {
    setAnchor(null);
  }, []);

  return {
    anchor,
    id,
    open,
    close,
    triggerProps: {
      'aria-controls': anchor === null ? undefined : id,
      'aria-haspopup': 'menu',
      'aria-expanded': anchor !== null,
    },
  };
};
